import { Formik } from "formik";
import { useState, useRef } from "react";
import CMSFormInput from "../formFields/CMSFormInput";
import { signInSchema } from "../utils/validations";
import FormButton from "../common/FormButton";
import ContainerLoading from "../common/loading/ContainerLoading";
import { useToast } from "../contexts/ToastContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "../../config/axios";
import { useCart } from "../contexts/CartContext";

const SigninForm = () => {
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "success" | "error"
  const [error, setError] = useState("");
  const { addToast } = useToast();
  const { fetchCart, cart } = useCart();
  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const from = params.get("from");
  // Refs for scrolling to first error
  const refs = {
    email: useRef(null),
    password: useRef(null),
  };

  // Scroll to first error field
  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    setTimeout(() => el.focus?.({ preventScroll: true }), 500);
  };

  // Separate submit function
  const handleSignIn = async (values) => {
    setStatus("loading");
    try {
      const response = await axios.post("/auth/signin", {
        email: values.email,
        password: values.password,
        cartId: values.cartId,
      });
      localStorage.removeItem("promoCodeSaved");
      localStorage.setItem("loggedInUser", JSON.stringify(response.data?.user));
      localStorage.setItem("cartId", response.data?.orderCartId);
      localStorage.setItem("mealCartId", response.data?.mealSubscriptionCartId);
      setIsLoggedIn(true);
      setStatus("success");
      fetchCart();
      if (from === "cart") {
        navigate(`/checkout/order`);
      } else {
        navigate("/account");
      }
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.message || "Sign in failed, please try again";
      setError(message);
      setTimeout(() => {
        setError("");
      }, 5000);
      setStatus("idle");
    }
  };

  return (
    <>
      <p className="text-34pt">Log In</p>
      <div className="mt-4 lg:mt-6 p-10 lg:p-20 bg-[#DEE2DD]/50 rounded-[15px] relative min-h-[300px] lg:max-w-[50%] 2.5xl:max-w-[40%]">
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
            <ContainerLoading />
          </div>
        )}

        <Formik
          initialValues={{
            email: "",
            password: "",
            cartId:
              from === "cart" && cart?.orderCart?._id
                ? cart?.orderCart?._id || ""
                : "",
          }}
          validationSchema={signInSchema}
          onSubmit={handleSignIn}
        >
          {(form) => (
            <div className="w-full grid grid-cols-1 gap-8">
              <div className="grid grid-cols-1 gap-8">
                <CMSFormInput
                  ref={refs.email}
                  name="email"
                  label="Email"
                  className="1"
                  form={form}
                  required
                />

                <CMSFormInput
                  ref={refs.password}
                  name="password"
                  label="Password"
                  className="1"
                  type="password"
                  form={form}
                  required
                />
              </div>
              {error && (
                <div
                  className={`relative transition-all duration-300 ease-in-out ${
                    error
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-2"
                  }`}
                >
                  {error && <span className="errorMessage">{error}</span>}
                </div>
              )}

              <div className="flex items-end justify-end mt-2">
                <FormButton
                  text="Log In"
                  type="submit"
                  className="text-lg px-4"
                  textColor="#fff"
                  onClick={() => {
                    form.handleSubmit();
                    setTimeout(() => scrollToFirstError(form.errors), 100);
                  }}
                />
              </div>

              <div className="flex flex-col mt-2">
                <a
                  className="text-18pt text-green hover:text-secondary font-mono leading-none mb-4 cursor-pointer"
                  title="Click to reset password"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </a>
                <a
                  className="text-18pt text-green hover:text-secondary font-mono cursor-pointer"
                  title="Click to create an account"
                  onClick={() => navigate("/signup")}
                >
                  Signup
                </a>
              </div>
            </div>
          )}
        </Formik>
      </div>
    </>
  );
};

export default SigninForm;
